import { useEffect, useMemo, useState } from 'react';
import { parseISO, differenceInDays, startOfDay } from 'date-fns';

import {
  useGetAllChatsQuery,
  useGetChatMessagesQuery,
  useCreateNewChatMutation,
} from 'redux/services/borrower-profile';
import {
  useGetB2cChatSessionsQuery,
  useGetB2cChatMessagesQuery,
} from 'redux/services/chatHistory';

import ChatHistoryModal from './ChatHistoryModal';
import styles from '@/styles/features/loan-information/chat-model/ChatHistory.module.css';

import { hasPermissions } from 'constants/permissionChecker';
import { getUserDetails } from 'auth/auth';
import tokenManager from 'services/auth/tokenManager';
import { useSelector } from 'react-redux';
import { userRoleData } from 'redux/slices/userRole';

const ChatHistory = ({
  isMaximized,
  setMessages,
  isVectorClicks,
  borrowerId,
  applicationId,
  setVectorClicks,
  setIsNewChat,
  setNewChatOpens,
  isNewChatOpens,
  setChatHistorySelected,
  setNewChatSelected,
  onActivateSession,
  buildChatMetadata,
  setIsHistoryLoading,
}) => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [selectedChatID, setSelectedChatID] = useState(null);
  const [groupedChats, setGroupedChats] = useState([]);

  const { userId, permissions } = getUserDetails();
  const role = useSelector(userRoleData);
  const isConsumer =
    hasPermissions(permissions, 'consumer') ||
    role?.userRole === 'consumer';

  const consumerOrgId = useMemo(() => {
    const user = tokenManager.getUserData() || {};
    return user.organizationId || user.organization_id || null;
  }, []);

  // ── B2B chat history queries (skip for consumers) ──
  const {
    data: b2bSessionList,
    refetch: refetchB2bHistory,
  } = useGetAllChatsQuery(
    { userId },
    { skip: isConsumer || !role?.userRole || !userId }
  );

  const { data: b2bMessages, refetch: refetchB2bMessages, isFetching: isB2bMsgFetching } =
    useGetChatMessagesQuery(
      { sessionId: selectedChatID, userId },
      { skip: !selectedChatID || !userId || isConsumer }
    );

  // ── B2C chat history queries (only for consumers) ──
  const {
    data: b2cSessionList,
    refetch: refetchB2cHistory,
  } = useGetB2cChatSessionsQuery(
    { consumerId: String(userId), clientOrgId: consumerOrgId ? String(consumerOrgId) : undefined },
    { skip: !isConsumer || !userId }
  );

  const { data: b2cMessages, refetch: refetchB2cMessages, isFetching: isB2cMsgFetching } =
    useGetB2cChatMessagesQuery(
      { sessionId: selectedChatID, consumerId: String(userId), clientOrgId: consumerOrgId ? String(consumerOrgId) : undefined },
      { skip: !selectedChatID || !userId || !isConsumer }
    );

  // ── Unified accessors ──
  const sessionList = isConsumer ? b2cSessionList : b2bSessionList;
  const getChatMessages = isConsumer ? b2cMessages : b2bMessages;
  const refetchAllChatHistory = isConsumer ? refetchB2cHistory : refetchB2bHistory;
  const refetchMessages = isConsumer ? refetchB2cMessages : refetchB2bMessages;
  const isMessagesFetching = isConsumer ? isB2cMsgFetching : isB2bMsgFetching;

  useEffect(() => {
    setIsHistoryLoading?.(isMessagesFetching && !!selectedChatID);
  }, [isMessagesFetching, selectedChatID, setIsHistoryLoading]);

  const [createNewChat] = useCreateNewChatMutation();

  //To change the response message structure to expected based on existing chatbot
  useEffect(() => {
    const messageList = getChatMessages?.messages ?? (Array.isArray(getChatMessages) ? getChatMessages : []);
    if (messageList.length > 0) {
      const transformedMessages = messageList.map((message) => {
        const sender = message?.role?.toLowerCase() === 'user' ? 'user' : 'AI';
        let content = message?.content ?? '';
        if (typeof content === 'string') {
          const trimmed = content.trim();
          if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            try {
              const parsed = JSON.parse(trimmed);
              const maybeMessage = parsed?.data?.message ?? parsed?.message;
              content = typeof maybeMessage === 'string' ? maybeMessage : trimmed;
            } catch (error) {
              // keep original string
            }
          }
        } else {
          content = JSON.stringify(content);
        }
        // The SPEECH section is deliberately LEFT IN PLACE here — the render
        // strips it for display and hands the text to the speaker button.
        // See the matching note in chat-model/index.jsx.
        // Keep id/feedback/versions so message actions and the version slider
        // hydrate on history selection (mirrors the parent transformHistoryMessages).
        const versions = message?.versions || null;
        return {
          sender,
          content: versions && versions.length ? versions[versions.length - 1] : content,
          isFromHistory: true,
          created_at: message?.created_at,
          id: message?.id,
          feedback: message?.feedback ?? null,
          versions,
          activeVersion: versions && versions.length ? versions.length - 1 : 0,
        };
      });
      setMessages(transformedMessages);
    }
  }, [getChatMessages, setMessages]);

  useEffect(() => {
    if (isVectorClicks) {
      onChatHistoryClicks();
    }
  }, [isVectorClicks]);

  useEffect(() => {
    if (!isVectorClicks) {
      setIsChatOpen(false);
      setNewChatOpens(false);
    }
  }, [isVectorClicks, setNewChatOpens]);

  // Update grouped chats when sessions change
  useEffect(() => {
    const chats = sessionList?.chats ?? (Array.isArray(sessionList) ? sessionList : sessionList?.data ?? []);
    setGroupedChats(groupChatsByRelativeTime(chats));
  }, [sessionList]);

  //Function to group the chats by date
  const getTimestamp = (chat) =>
    chat?.last_message_at ?? chat?.metadata?.last_message_at ?? chat?.created_at;

  const groupChatsByRelativeTime = (chats) => {
    const now = startOfDay(new Date());
    const groupedChats = {
      Today: [],
      Yesterday: [],
      Earlier: [],
    };
    const sortedChats = Array.isArray(chats)
      ? [...chats].sort((a, b) => {
          const tsA = getTimestamp(a);
          const tsB = getTimestamp(b);
          const dateA = tsA ? new Date(tsA).getTime() : 0;
          const dateB = tsB ? new Date(tsB).getTime() : 0;
          return dateB - dateA;
        })
      : [];
    sortedChats.forEach((chat) => {
      const timestamp = getTimestamp(chat);
      const createdAt = timestamp ? parseISO(timestamp) : null;
      const chatDate = createdAt ? startOfDay(createdAt) : null;
      if (!chatDate) {
        groupedChats.Earlier.push(chat);
        return;
      }
      const daysDifference = differenceInDays(now, chatDate);
      if (daysDifference === 0) {
        groupedChats.Today.push(chat);
      } else if (daysDifference === 1) {
        groupedChats.Yesterday.push(chat);
      } else {
        groupedChats.Earlier.push(chat);
      }
    });
    return groupedChats;
  };

  //Function that triggers when the Chat History button clicks
  const onChatHistoryClicks = async () => {
    setNewChatSelected(false);
    setNewChatOpens(true);
    setIsChatOpen(true);
    try {
      const { data } = await refetchAllChatHistory();
      const sessions = data?.chats ?? (Array.isArray(data) ? data : data?.data ?? []);
      const updatedGroupedChats = groupChatsByRelativeTime(sessions);
      setGroupedChats(updatedGroupedChats);
    } catch (error) {
      console.error('Error fetching chat history:', error);
    }
  };

  //Function that triggers when selected a particular chat
  const setChatWindowOpens = (sessionId) => {
    setChatHistorySelected(true);
    // Show skeleton immediately — RTK Query's isFetching stays false on cache
    // hits, so relying on it alone makes the skeleton flicker or never appear.
    setIsHistoryLoading?.(true);
    setSelectedChatID(sessionId);
    refetchMessages();
    setIsChatOpen(false);
    setVectorClicks(false);
    setNewChatOpens(false);
    setMessages([]);
    if (sessionId) {
      onActivateSession?.(sessionId, {
        broadcast: true,
        resetMessages: true,
      });
    }
  };

  //function that triggers when the New Chat clicks
  const newChatAction = async () => {
    setIsNewChat(true);
    setIsChatOpen(false);
    setVectorClicks(false);
    setMessages([]);
    setChatHistorySelected(false);

    if (isConsumer) {
      // B2C: new session is created by the WebSocket on next message
      onActivateSession?.(null, { broadcast: true, resetMessages: true });
      return;
    }

    const metadataPayload =
      typeof buildChatMetadata === 'function'
        ? buildChatMetadata()
        : {
            source: 'chat-ui',
            initiatedAt: new Date().toISOString(),
          };

    const response = await createNewChat({
      borrower_id: borrowerId,
      loan_application_id: applicationId,
      metadata: metadataPayload,
    });
    const chatId = response?.data?.id ?? response?.id ?? null;
    if (chatId) {
      setSelectedChatID(chatId);
      onActivateSession?.(chatId, {
        broadcast: true,
        resetMessages: true,
      });
      await refetchAllChatHistory();
    } else {
      // REST failed (e.g. 401/503) — fall back to WebSocket session creation.
      // Sending null tells the backend to create a brand-new session.
      onActivateSession?.(null, { broadcast: true, resetMessages: true });
    }
  };

  const isPanelVisible = isVectorClicks && isChatOpen;
  const isHistoryLoadingFlag = isMessagesFetching && !!selectedChatID;

  return (
    <>
      {isPanelVisible && (
        <div className={`${styles.sidebar} ${isPanelVisible ? styles.open : ''}`}>
          <ChatHistoryModal
            isChatOpen={isChatOpen}
            isVectorClicks={isVectorClicks}
            setIsChatOpen={setIsChatOpen}
            newChatAction={newChatAction}
            setChatWindowOpens={setChatWindowOpens}
            groupedChats={groupedChats}
            setVectorClicks={setVectorClicks}
            setNewChatOpens={setNewChatOpens}
            setNewChatSelected={setNewChatSelected}
            isHistoryLoading={isHistoryLoadingFlag}
          />
        </div>
      )}
    </>
  );
};

export default ChatHistory;
